import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../../../src/assets/img/logo.png";
import { useDispatch, useSelector } from "react-redux";
import {
  sidebarMenuList,
  sidebarMenuListForOrgController,
  sidebarMenuListForOrgLeader,
  sidebarMenuListForUser,
} from "../../../utils/contstant";
import { SidebarMenuType } from "../../../types/global";
import { userLogout } from "../../../actions/user";
import { getRedirectPath, tabTitle } from "../../../utils/comman";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

// Define interface for notification badge props
interface NotificationBadgeProps {
  itemName: string;
  count: number;
}

// Define interface for notifications
interface NotificationList {
  referralsCount?: number;
  referrersCount?: number;
  businessCount?: number;
  referHeliosCount?: number;
  rewards?: number;
  contact?: number;
}

// Define interface for user
interface UserData {
  user?: {
    roleId?: number;
  };
}

// Define interface for sidebar item props
interface SidebarItemProps {
  item: SidebarMenuType;
  user: UserData;
  notifications: NotificationList;
  handleremoveBodyClass: () => void;
}

// Extract notification badge rendering to reduce complexity
const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  itemName,
  count,
}) => {
  if (count <= 0) return null;

  return (
    <div className="badge-icon">
      <span
        className={
          itemName === "My Referrals" || itemName === "My Referrers"
            ? "custom-badge"
            : "custom-badge-lg"
        }
      >
        {count}
      </span>
    </div>
  );
};

// Extract title setting logic to a separate function
const setDocumentTitle = (path: string) => {
  // Direct match from tabTitle
  let currentTitle = tabTitle[path];

  if (!currentTitle) {
    // Define route patterns and their respective titles
    const routeMapping: { [key: string]: string } = {
      "/admin/view/user/": "User",
      "/admin/view/data-element/": "Data Element",
      "/admin/view-organization/": "Organization",
      "/admin/data-entry/": "Data Entry",
      "/leader/view-organization/": "Organization",
      "/controller/view-organization/": "Organization",
      "/leader/data-entry": "Data Entry",
      "/controller/data-entry": "Data Entry",
    };

    // Find a matching key
    currentTitle =
      Object.entries(routeMapping).find(([pattern]) =>
        path.startsWith(pattern)
      )?.[1] || "Helios"; // Default title
  }

  document.title = `Helios | ${currentTitle}`;
};

// Extract sidebar item rendering to a separate component
const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  user,
  notifications,
  handleremoveBodyClass,
}) => {
  // Early return if role doesn't match
  if (!item?.roleId?.includes(user?.user?.roleId || 0)) return null;

  // Get the notification count based on the item name
  const getNotificationCount = (itemName: string): number => {
    if (!notifications) return 0;

    const notificationMap: Record<string, number> = {
      "My Referrals": notifications?.referralsCount || 0,
      "My Referrers": notifications?.referrersCount || 0,
      Businesses: notifications?.businessCount || 0,
      "Refer Helios": notifications?.referHeliosCount || 0,
      "Helios Network": notifications?.rewards || 0,
      "Contact Us": notifications?.contact || 0,
    };

    return notificationMap[itemName] || 0;
  };

  return (
    <NavLink
      key={item.id}
      className="sideNavigation rounded-r-[5px] py-[15px] pl-[53px]"
      to={item.path}
      onClick={handleremoveBodyClass}
    >
      {item.icon}
      <span>{item.name}</span>
      <NotificationBadge
        itemName={item.name}
        count={getNotificationCount(item.name)}
      />
    </NavLink>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const user: UserData = useSelector((state: any) => state?.Auth?.user);
  const referralNotificationList: NotificationList = useSelector(
    (state: any) => state?.Business?.referralNotificationList
  );
  const [sideBarList, setSidebarList] =
    useState<SidebarMenuType[]>(sidebarMenuList);

  const trigger = useRef<HTMLDivElement>(null);
  const sidebar = useRef<HTMLDivElement>(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  // Set document title based on route
  useEffect(() => {
    setDocumentTitle(location?.pathname);
  }, [location?.pathname]);

  // Set sidebar menu based on user role
  useEffect(() => {
    const roleMenuMap: Record<number, SidebarMenuType[]> = {
      1: sidebarMenuList,
      2: sidebarMenuListForOrgLeader,
      3: sidebarMenuListForOrgController,
      4: sidebarMenuListForUser,
    };

    const roleId = user?.user?.roleId;
    if (roleId !== undefined && roleMenuMap[roleId]) {
      setSidebarList(roleMenuMap[roleId]);
    }
  }, [user?.user?.roleId]);

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target as Node) ||
        trigger.current.contains(target as Node)
      )
        return;

      setSidebarOpen(false);
    };

    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };

    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // Handle sidebar expanded state
  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    const bodyClassList = document.querySelector("body")?.classList;

    if (sidebarExpanded) {
      bodyClassList?.add("sidebar-expanded");
    } else {
      bodyClassList?.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  const handleremoveBodyClass = () => {
    document.body.classList.remove("sidebarOpen");
  };

  const logout = () => {
    dispatch(
      userLogout(() => {
        navigate(getRedirectPath());
      }) as any
    );
  };

  return (
    <div className="sidebar bg-[#001B2C] border border-[#2E2E2E] flex flex-col rounded-r-[25px] py-[20px] pr-[20px]">
      <div className="upper-col flex-1">
        <div className="mainlogo">
          <img
            src={Logo}
            alt="logo"
            className="h-[95px] w-[85px] mx-auto mb-[20px]"
          />
          <button className="btnsClosedSidebar">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
            >
              <path
                d="M7.50001 14.5L11 11M11 11L14.5 7.49999M11 11L14.5 14.5M11 11L7.50001 7.49999M20.5 11C20.5 16.2467 16.2467 20.5 11 20.5C5.75329 20.5 1.5 16.2467 1.5 11C1.5 5.75329 5.75329 1.5 11 1.5C16.2467 1.5 20.5 5.75329 20.5 11Z"
                stroke="#35353A"
                strokeWidth="1.25"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <span className="logo-bottom-line block h-px"> </span>
        <div className="navigationMenu mt-[20px]">
          <nav id="sidebar" className="accordianmenu">
            {sideBarList?.map((item: SidebarMenuType) => (
              <SidebarItem
                key={item.id}
                item={item}
                user={user}
                notifications={referralNotificationList || {}}
                handleremoveBodyClass={handleremoveBodyClass}
              />
            ))}
          </nav>
        </div>
      </div>

      <div className="bottom-col">
        <div
          className="flex gap-[15px] pl-[50px] items-center cursor-pointer mt-[30px]"
          onClick={logout}
        >
          <svg
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.5 0C10.0277 0 7.61099 0.733112 5.55538 2.10663C3.49976 3.48015 1.89761 5.43238 0.951511 7.71646C0.00541617 10.0005 -0.242126 12.5139 0.24019 14.9386C0.722505 17.3634 1.91301 19.5907 3.66117 21.3388C5.40933 23.087 7.63661 24.2775 10.0614 24.7598C12.4861 25.2421 14.9995 24.9946 17.2835 24.0485C19.5676 23.1024 21.5199 21.5002 22.8934 19.4446C24.2669 17.389 25 14.9723 25 12.5C24.996 9.18601 23.6778 6.00889 21.3345 3.66555C18.9911 1.3222 15.814 0.00396964 12.5 0ZM11.6667 5.83333C11.6667 5.61232 11.7545 5.40036 11.9107 5.24408C12.067 5.0878 12.279 5 12.5 5C12.721 5 12.933 5.0878 13.0893 5.24408C13.2455 5.40036 13.3333 5.61232 13.3333 5.83333V11.6667C13.3333 11.8877 13.2455 12.0996 13.0893 12.2559C12.933 12.4122 12.721 12.5 12.5 12.5C12.279 12.5 12.067 12.4122 11.9107 12.2559C11.7545 12.0996 11.6667 11.8877 11.6667 11.6667V5.83333ZM12.5 20C10.5118 19.9969 8.60597 19.2057 7.20011 17.7999C5.79426 16.394 5.00309 14.4882 5 12.5C4.99636 11.3753 5.24796 10.2645 5.73585 9.25121C6.22375 8.23789 6.93521 7.34849 7.81667 6.65C7.90059 6.57079 8.00004 6.50987 8.10873 6.4711C8.21741 6.43233 8.33297 6.41656 8.44807 6.42478C8.56317 6.43301 8.67531 6.46504 8.77738 6.51887C8.87946 6.57269 8.96924 6.64713 9.04104 6.73746C9.11285 6.8278 9.16511 6.93206 9.19451 7.04364C9.22392 7.15523 9.22983 7.27171 9.21187 7.38569C9.19391 7.49968 9.15247 7.6087 9.09018 7.70584C9.02789 7.80298 8.9461 7.88612 8.85 7.95C7.90762 8.70593 7.22318 9.73581 6.89122 10.8974C6.55925 12.059 6.59613 13.295 6.99677 14.4348C7.3974 15.5745 8.14203 16.5617 9.12781 17.2601C10.1136 17.9585 11.2919 18.3336 12.5 18.3336C13.7081 18.3336 14.8864 17.9585 15.8722 17.2601C16.858 16.5617 17.6026 15.5745 18.0032 14.4348C18.4039 13.295 18.4408 12.059 18.1088 10.8974C17.7768 9.73581 17.0924 8.70593 16.15 7.95C15.9982 7.80669 15.9057 7.61162 15.8908 7.40338C15.8759 7.19513 15.9398 6.98889 16.0697 6.82546C16.1996 6.66203 16.3861 6.55331 16.5923 6.52082C16.7986 6.48833 17.0095 6.53443 17.1833 6.65C18.0648 7.34849 18.7763 8.23789 19.2642 9.25121C19.752 10.2645 20.0036 11.3753 20 12.5C19.9969 14.4882 19.2057 16.394 17.7999 17.7999C16.394 19.2057 14.4882 19.9969 12.5 20Z"
              fill="#48BA89"
            />
          </svg>

          <h4 className="text-sm text-[#48BA89]">Logout</h4>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
